// Huntable animals (M13 Phase 3): chickens, goats, sheep. Deterministic herds
// per 500 m cell in meadow/farmland, streamed near the player. Procedural
// low-poly meshes (NOT blocky), simple brains: graze → wander → flee. They
// take melee and bullet damage, fall over dead, and butcher into raw meat.
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Vector3, Color3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Clamp } from '@babylonjs/core/Maths/math.scalar.functions';
import { meleeSwing } from './melee-arc.mjs';

const HERD_CELL = 500;
const SPECIES = {
  chicken: { hp: 12, walk: 0.7, run: 2.8, scareR: 6, meat: ['meat_raw_chicken', 2], label: 'Chicken', voice: 'animal.chicken', alarm: 'animal.chicken_alarm' },
  goat: { hp: 45, walk: 0.9, run: 4.2, scareR: 9, meat: ['meat_raw_mutton', 2], label: 'Goat', voice: 'animal.goat', alarm: 'animal.goat' },
  sheep: { hp: 45, walk: 0.8, run: 3.6, scareR: 8, meat: ['meat_raw_mutton', 3], label: 'Sheep', voice: 'animal.sheep', alarm: 'animal.sheep' },
  wolf: {
    hp: 70, walk: 1.1, run: 5.2, scareR: 0, predator: true,
    meat: ['meat_raw_game', 2], label: 'Wolf',
    voice: 'amb.wolf', alarm: 'creature.dog',
    biteRange: 1.9, biteCd: 1.3, biteDmg: [7, 13], bleedChance: 0.6,
  },
};
const EARSHOT = 55; // m — calls beyond this are skipped entirely

function hash2(x, z, salt) {
  let h = (x * 374761393 + z * 668265263 + salt * 971) | 0;
  h = (h ^ (h >> 13)) * 1274126177;
  return (((h ^ (h >> 16)) >>> 0) % 100000) / 100000;
}

export class Animals {
  constructor(scene, terrain, veg, sfx) {
    this.scene = scene;
    this.terrain = terrain;
    this.veg = veg;
    this.sfx = sfx;
    // set by main.js right after `world` exists — meleeHit reads world.query
    // (occlusion) and world.player.attack (the swing id) off it
    this.world = null;
    this.animals = [];        // live + corpse entries
    this.dead = new Set();    // "herdKey_i" killed this session (stay dead)
    this.butchered = new Set();
    this.herds = new Map();   // herdKey → true (spawned)
    this.templates = {};
    this._buildTemplates();
    this._streamT = 9;
  }

  _mat(name, hex) {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = Color3.FromHexString(hex);
    m.emissiveColor = m.diffuseColor.scale(0.12);
    m.specularColor = Color3.Black();
    return m;
  }

  // one merged multi-material mesh per species; clones share geometry
  _buildTemplates() {
    const S = this.scene;
    const parts = [];
    const add = (mesh, mat) => { mesh.material = mat; parts.push(mesh); return mesh; };
    const finish = (name) => {
      const merged = Mesh.MergeMeshes(parts.splice(0), true, true, undefined, false, true);
      merged.name = name;
      merged.setEnabled(false);
      merged.isPickable = false;
      return merged;
    };

    // ---- chicken (~0.4 m) ----
    const white = this._mat('an_white', '#ddd6c4');
    const red = this._mat('an_red', '#b03028');
    const orange = this._mat('an_orange', '#c07828');
    let b = add(MeshBuilder.CreateSphere('c_body', { diameterX: 0.34, diameterY: 0.3, diameterZ: 0.44, segments: 6 }, S), white);
    b.position.set(0, 0.26, 0);
    b = add(MeshBuilder.CreateSphere('c_head', { diameter: 0.16, segments: 4 }, S), white);
    b.position.set(0, 0.47, 0.2);
    b = add(MeshBuilder.CreateCylinder('c_beak', { height: 0.09, diameterBottom: 0.05, diameterTop: 0.005, tessellation: 4 }, S), orange);
    b.rotation.x = Math.PI / 2; b.position.set(0, 0.46, 0.3);
    b = add(MeshBuilder.CreateBox('c_comb', { width: 0.03, height: 0.07, depth: 0.1 }, S), red);
    b.position.set(0, 0.56, 0.19);
    b = add(MeshBuilder.CreateSphere('c_tail', { diameterX: 0.12, diameterY: 0.18, diameterZ: 0.16, segments: 4 }, S), white);
    b.position.set(0, 0.34, -0.2); b.rotation.x = -0.5;
    for (const s of [-1, 1]) {
      b = add(MeshBuilder.CreateCylinder('c_leg', { height: 0.16, diameter: 0.022, tessellation: 4 }, S), orange);
      b.position.set(s * 0.06, 0.08, 0.02);
    }
    this.templates.chicken = finish('animal_chicken');

    // ---- sheep (~0.95 m at back) ----
    const wool = this._mat('an_wool', '#cfc8b4');
    const dark = this._mat('an_dark', '#3a332c');
    b = add(MeshBuilder.CreateSphere('s_body', { diameterX: 0.72, diameterY: 0.66, diameterZ: 1.1, segments: 6 }, S), wool);
    b.position.set(0, 0.62, 0);
    b = add(MeshBuilder.CreateSphere('s_head', { diameterX: 0.22, diameterY: 0.26, diameterZ: 0.3, segments: 4 }, S), dark);
    b.position.set(0, 0.82, 0.58);
    for (const s of [-1, 1]) {
      b = add(MeshBuilder.CreateSphere('s_ear', { diameterX: 0.1, diameterY: 0.05, diameterZ: 0.06, segments: 3 }, S), dark);
      b.position.set(s * 0.13, 0.88, 0.55);
      b = add(MeshBuilder.CreateCylinder('s_legF', { height: 0.42, diameter: 0.07, tessellation: 5 }, S), dark);
      b.position.set(s * 0.18, 0.21, 0.34);
      b = add(MeshBuilder.CreateCylinder('s_legB', { height: 0.42, diameter: 0.07, tessellation: 5 }, S), dark);
      b.position.set(s * 0.18, 0.21, -0.36);
    }
    this.templates.sheep = finish('animal_sheep');

    // ---- goat (~0.95 m) ----
    const hide = this._mat('an_hide', '#8a7862');
    const horn = this._mat('an_horn', '#5c5044');
    b = add(MeshBuilder.CreateSphere('g_body', { diameterX: 0.5, diameterY: 0.56, diameterZ: 1.0, segments: 6 }, S), hide);
    b.position.set(0, 0.6, 0);
    b = add(MeshBuilder.CreateSphere('g_head', { diameterX: 0.2, diameterY: 0.28, diameterZ: 0.34, segments: 4 }, S), hide);
    b.position.set(0, 0.92, 0.5); b.rotation.x = 0.35;
    for (const s of [-1, 1]) {
      b = add(MeshBuilder.CreateCylinder('g_horn', { height: 0.16, diameterBottom: 0.045, diameterTop: 0.008, tessellation: 4 }, S), horn);
      b.rotation.x = -0.8; b.position.set(s * 0.07, 1.06, 0.42);
      b = add(MeshBuilder.CreateSphere('g_ear', { diameterX: 0.12, diameterY: 0.04, diameterZ: 0.07, segments: 3 }, S), hide);
      b.position.set(s * 0.14, 0.98, 0.48);
      b = add(MeshBuilder.CreateCylinder('g_legF', { height: 0.44, diameter: 0.06, tessellation: 5 }, S), hide);
      b.position.set(s * 0.14, 0.22, 0.3);
      b = add(MeshBuilder.CreateCylinder('g_legB', { height: 0.44, diameter: 0.06, tessellation: 5 }, S), hide);
      b.position.set(s * 0.14, 0.22, -0.32);
    }
    b = add(MeshBuilder.CreateSphere('g_tail', { diameterX: 0.06, diameterY: 0.12, diameterZ: 0.06, segments: 3 }, S), dark);
    b.position.set(0, 0.78, -0.5);
    this.templates.goat = finish('animal_goat');

    // ---- wolf (~0.8 m at the shoulder, long and grey) ----
    const grey = this._mat('an_grey', '#5c5a56');
    const greyD = this._mat('an_greyd', '#3e3c38');
    b = add(MeshBuilder.CreateSphere('w_body', { diameterX: 0.42, diameterY: 0.5, diameterZ: 1.15, segments: 6 }, S), grey);
    b.position.set(0, 0.58, 0);
    b = add(MeshBuilder.CreateSphere('w_head', { diameterX: 0.2, diameterY: 0.22, diameterZ: 0.3, segments: 4 }, S), grey);
    b.position.set(0, 0.74, 0.58);
    b = add(MeshBuilder.CreateCylinder('w_snout', { height: 0.2, diameterBottom: 0.14, diameterTop: 0.06, tessellation: 5 }, S), greyD);
    b.rotation.x = Math.PI / 2; b.position.set(0, 0.7, 0.76);
    for (const s of [-1, 1]) {
      b = add(MeshBuilder.CreateCylinder('w_ear', { height: 0.12, diameterBottom: 0.07, diameterTop: 0.005, tessellation: 4 }, S), greyD);
      b.position.set(s * 0.07, 0.88, 0.52);
      b = add(MeshBuilder.CreateCylinder('w_legF', { height: 0.46, diameter: 0.06, tessellation: 5 }, S), grey);
      b.position.set(s * 0.11, 0.23, 0.38);
      b = add(MeshBuilder.CreateCylinder('w_legB', { height: 0.46, diameter: 0.06, tessellation: 5 }, S), grey);
      b.position.set(s * 0.11, 0.23, -0.38);
    }
    b = add(MeshBuilder.CreateSphere('w_tail', { diameterX: 0.08, diameterY: 0.09, diameterZ: 0.42, segments: 4 }, S), greyD);
    b.position.set(0, 0.6, -0.68); b.rotation.x = -0.4;
    this.templates.wolf = finish('animal_wolf');
  }

  // deterministic herd plan for a cell (or null): farm animals graze the open
  // country, wolf packs prowl the forests and the alpine zone
  herdPlan(cx, cz) {
    const x = (cx + 0.25 + hash2(cx, cz, 102) * 0.5) * HERD_CELL;
    const z = (cz + 0.25 + hash2(cx, cz, 103) * 0.5) * HERD_CELL;
    const biome = this.veg.biomeAt(x, z);
    if (this.terrain.getHeight(x, z) < 0.5) return null;
    if (biome === 2 || biome === 3) {
      if (hash2(cx, cz, 101) > 0.4) return null;
      const r = hash2(cx, cz, 104);
      const species = biome === 3
        ? (r < 0.5 ? 'chicken' : r < 0.75 ? 'goat' : 'sheep')
        : (r < 0.45 ? 'sheep' : r < 0.8 ? 'goat' : 'chicken');
      const n = species === 'chicken' ? 4 + Math.floor(hash2(cx, cz, 105) * 3)
        : 3 + Math.floor(hash2(cx, cz, 105) * 3);
      return { key: `${cx}_${cz}`, species, x, z, n };
    }
    if ((biome >= 4 && biome <= 6) || biome === 9) {
      if (hash2(cx, cz, 120) > 0.15) return null;
      return { key: `${cx}_${cz}`, species: 'wolf', x, z, n: 2 + Math.floor(hash2(cx, cz, 121) * 3) };
    }
    return null;
  }

  _spawnHerd(plan) {
    this.herds.set(plan.key, true);
    if (plan.species === 'wolf' && this.netWolves) return; // server owns the packs online
    for (let i = 0; i < plan.n; i++) {
      const id = `${plan.key}_${i}`;
      if (this.dead.has(id) && this.butchered.has(id)) continue;
      const ax = plan.x + (hash2(plan.n, i, 106) - 0.5) * 24;
      const az = plan.z + (hash2(plan.n, i, 107) - 0.5) * 24;
      const mesh = this.templates[plan.species].clone('an_' + id);
      mesh.setEnabled(true);
      mesh.isPickable = true;
      const sp = SPECIES[plan.species];
      const a = {
        id, herd: plan.key, species: plan.species, sp, mesh,
        pos: new Vector3(ax, this.terrain.getHeight(ax, az), az),
        yaw: hash2(i, plan.n, 108) * Math.PI * 2,
        anchor: new Vector3(plan.x, 0, plan.z),
        hp: sp.hp, state: 'graze', t: 1 + hash2(i, 3, 109) * 4,
        callT: 2 + hash2(i, 9, 112) * 12,
        target: null, fleeDir: null, bob: hash2(i, 5, 110) * 6,
        deadT: this.dead.has(id) ? 1 : 0, // already fell over in a past visit
      };
      if (this.dead.has(id)) a.state = 'dead';
      mesh.metadata = { animal: a };
      this.animals.push(a);
      this._pose(a, 0);
    }
  }

  // distance-faded, camera-panned call. Long recordings (a whole minute of
  // clucking) play a short sliced window so one call = one utterance.
  _call(a, role, base = 0.45, rate = 1) {
    const p = this._playerPos;
    if (!p || !role) return;
    const d = Math.hypot(a.pos.x - p.x, a.pos.z - p.z);
    if (d > EARSHOT) return;
    const vol = base * Math.max(0.08, 1 - d / EARSHOT);
    const pan = this._camYaw == null ? 0
      : Math.max(-0.8, Math.min(0.8, Math.sin(Math.atan2(a.pos.x - p.x, a.pos.z - p.z) - this._camYaw)));
    this.sfx?.play?.(role, { vol, rate, jitter: 0.12, slice: 1.4, pan });
  }

  // ---- server-simulated wolves (multiplayer): mirror the 5 Hz snapshot ----
  applyServerWolves(list) {
    const ST = ['graze', 'walk', 'attack', 'flee', 'dead'];
    if (!this._purgedLocalWolves) {
      this._purgedLocalWolves = true;
      for (let i = this.animals.length - 1; i >= 0; i--) {
        const a = this.animals[i];
        if (a.species === 'wolf' && !a.remote) { a.mesh.dispose(); this.animals.splice(i, 1); }
      }
    }
    const seen = new Set();
    for (const [id, x, z, yaw, st] of list) {
      seen.add(id);
      let a = this.animals.find((w) => w.id === id);
      if (!a) {
        if (this.butchered.has(id)) continue;
        const mesh = this.templates.wolf.clone('an_' + id);
        mesh.setEnabled(true);
        mesh.isPickable = true;
        a = {
          id, remote: true, species: 'wolf', sp: SPECIES.wolf, mesh,
          pos: new Vector3(x, this.terrain.getHeight(x, z), z),
          yaw, state: ST[st] || 'graze', hp: SPECIES.wolf.hp,
          bob: Math.random() * 6, callT: 6 + Math.random() * 16,
          deadT: st === 4 ? 1 : 0,
          netX: x, netZ: z, netYaw: yaw,
        };
        mesh.metadata = { animal: a };
        this.animals.push(a);
        this._pose(a, 0);
      }
      a.netX = x; a.netZ = z; a.netYaw = yaw;
      const ns = ST[st] || 'graze';
      if (ns === 'attack' && a.state !== 'attack') this._call(a, a.sp.alarm, 0.6, 0.9);
      if (ns === 'dead' && a.state !== 'dead') this.dead.add(id);
      a.state = ns;
    }
    // wolves the server no longer tracks (despawned or butchered elsewhere)
    for (let i = this.animals.length - 1; i >= 0; i--) {
      const a = this.animals[i];
      if (a.remote && !seen.has(a.id)) { a.mesh.dispose(); this.animals.splice(i, 1); }
    }
  }

  _updateRemote(a, dt) {
    if (a.state === 'dead') {
      if (a.deadT < 1) a.deadT = Math.min(1, a.deadT + dt * 2.5);
      this._pose(a, dt);
      return;
    }
    // glide toward the latest server position (5 Hz snapshots)
    const k = Math.min(1, dt * 6);
    if (Math.hypot(a.netX - a.pos.x, a.netZ - a.pos.z) > 30) a.pos.set(a.netX, a.pos.y, a.netZ);
    else { a.pos.x += (a.netX - a.pos.x) * k; a.pos.z += (a.netZ - a.pos.z) * k; }
    let dy = (a.netYaw - a.yaw) % (Math.PI * 2);
    if (dy > Math.PI) dy -= Math.PI * 2;
    if (dy < -Math.PI) dy += Math.PI * 2;
    a.yaw += dy * k;
    a.bob += dt * (a.state === 'attack' || a.state === 'flee' ? 14 : a.state === 'walk' ? 7 : 2);
    a.callT -= dt;
    if (a.callT <= 0) {
      a.callT = a.state === 'attack' ? 2 + Math.random() * 3 : 10 + Math.random() * 25;
      this._call(a, a.state === 'attack' ? a.sp.alarm : a.sp.voice, a.state === 'attack' ? 0.55 : 0.4);
    }
    this._pose(a, dt);
  }

  update(dt, playerPos, playerSprinting, camYaw, playerDead = false) {
    this._playerPos = playerPos;
    this._camYaw = camYaw;
    this._playerDead = playerDead;
    // stream herds around the player every couple of seconds
    this._streamT += dt;
    if (this._streamT > 2) {
      this._streamT = 0;
      const c0x = Math.floor((playerPos.x - 400) / HERD_CELL), c1x = Math.floor((playerPos.x + 400) / HERD_CELL);
      const c0z = Math.floor((playerPos.z - 400) / HERD_CELL), c1z = Math.floor((playerPos.z + 400) / HERD_CELL);
      for (let cz = c0z; cz <= c1z; cz++) {
        for (let cx = c0x; cx <= c1x; cx++) {
          const plan = this.herdPlan(cx, cz);
          if (plan && !this.herds.has(plan.key)) this._spawnHerd(plan);
        }
      }
      for (let i = this.animals.length - 1; i >= 0; i--) {
        const a = this.animals[i];
        if (a.remote) continue; // server decides when its wolves despawn
        if (Math.hypot(a.pos.x - playerPos.x, a.pos.z - playerPos.z) > 550) {
          a.mesh.dispose();
          this.animals.splice(i, 1);
          this.herds.delete(a.herd); // whole herd respawns on return
        }
      }
    }

    for (const a of this.animals) {
      // re-snap to the ground every frame — terrain LOD streams in after spawn
      a.pos.y = this.terrain.getHeight(a.pos.x, a.pos.z);
      if (a.remote) { this._updateRemote(a, dt); continue; }
      if (a.state === 'dead') {
        if (a.deadT < 1) { a.deadT = Math.min(1, a.deadT + dt * 2.5); this._pose(a, dt); }
        continue;
      }
      a.t -= dt;
      const toPlayer = Math.hypot(playerPos.x - a.pos.x, playerPos.z - a.pos.z);
      const threat = playerSprinting ? a.sp.scareR * 1.8 : a.sp.scareR;
      if (!a.sp.predator && toPlayer < threat && a.state !== 'flee') this._flee(a, playerPos);

      // predators: sometimes they decide you look like dinner
      if (a.sp.predator && a.state !== 'flee') {
        if (a.state !== 'attack') {
          a.aggroT = (a.aggroT ?? 1 + hash2(a.bob * 50 | 0, 3, 130) * 2) - dt;
          if (toPlayer < 28 && !this._playerDead && a.aggroT <= 0) {
            a.aggroT = 2 + Math.random() * 2;
            if (Math.random() < 0.3 || (playerSprinting && toPlayer < 14)) {
              a.state = 'attack';
              this._call(a, a.sp.alarm, 0.6, 0.9); // low growl — it's coming
            }
          }
        }
        if (a.state === 'attack') {
          if (this._playerDead || toPlayer > 60) {
            a.state = 'graze'; a.t = 2;
            a.anchor.set(a.pos.x, 0, a.pos.z);
          } else {
            this._step(a, playerPos, a.sp.run, dt);
            a.biteT = (a.biteT ?? 0.6) - dt;
            if (toPlayer < a.sp.biteRange && a.biteT <= 0) {
              a.biteT = a.sp.biteCd;
              this._call(a, a.sp.alarm, 0.65, 1.15);
              this.onBite?.(a);
            }
          }
        }
      }

      // idle chatter / panic calls (occasional rooster among the hens)
      a.callT -= dt;
      if (a.callT <= 0) {
        a.callT = a.state === 'flee' ? 2 + Math.random() * 3 : 6 + Math.random() * 16;
        const rooster = a.species === 'chicken' && a.state !== 'flee' && Math.random() < 0.15;
        this._call(a,
          rooster ? 'animal.rooster' : a.state === 'flee' ? a.sp.alarm : a.sp.voice,
          a.state === 'flee' ? 0.55 : 0.4,
          a.state === 'flee' && a.species !== 'chicken' ? 1.15 : 1);
      }

      if (a.state === 'graze' && a.t <= 0) {
        a.state = 'walk'; a.t = 3 + hash2(a.bob * 100 | 0, this.animals.length, 111) * 4;
        const ang = Math.random() * Math.PI * 2;
        const r = 4 + Math.random() * 10;
        a.target = new Vector3(a.anchor.x + Math.sin(ang) * r, 0, a.anchor.z + Math.cos(ang) * r);
      } else if (a.state === 'walk') {
        if (this._step(a, a.target, a.sp.walk, dt) || a.t <= 0) { a.state = 'graze'; a.t = 2 + Math.random() * 5; }
      } else if (a.state === 'flee') {
        const away = new Vector3(a.pos.x - playerPos.x, 0, a.pos.z - playerPos.z).normalize();
        if (a.species === 'chicken') { // panicked zigzag
          const wob = Math.sin(performance.now() / 180 + a.bob) * 0.8;
          const c = Math.cos(wob), s = Math.sin(wob);
          away.set(away.x * c - away.z * s, 0, away.x * s + away.z * c);
        }
        const tgt = a.pos.add(away.scale(8));
        this._step(a, tgt, a.sp.run, dt);
        if (a.t <= 0 && toPlayer > threat + 4) { a.state = 'graze'; a.t = 2; a.anchor.set(a.pos.x, 0, a.pos.z); }
      }
      a.bob += dt * (a.state === 'flee' || a.state === 'attack' ? 14 : a.state === 'walk' ? 7 : 2);
      this._pose(a, dt);
    }
  }

  _step(a, target, speed, dt) {
    const dx = target.x - a.pos.x, dz = target.z - a.pos.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.4) return true;
    const wantYaw = Math.atan2(dx, dz);
    let dy = (wantYaw - a.yaw) % (Math.PI * 2);
    if (dy > Math.PI) dy -= Math.PI * 2;
    if (dy < -Math.PI) dy += Math.PI * 2;
    a.yaw += Clamp(dy, -3 * dt, 3 * dt);
    const nx = a.pos.x + Math.sin(a.yaw) * speed * dt;
    const nz = a.pos.z + Math.cos(a.yaw) * speed * dt;
    const ny = this.terrain.getHeight(nx, nz);
    if (ny < 0.3) { a.yaw += Math.PI / 2; return true; } // never into the sea
    a.pos.set(nx, ny, nz);
    return false;
  }

  _pose(a, dt) {
    const m = a.mesh;
    m.position.copyFrom(a.pos);
    m.rotation.y = a.yaw;
    if (a.state === 'dead') {
      // keel over sideways, settle slightly into the grass
      m.rotation.z = (Math.PI / 2) * a.deadT;
      m.position.y = a.pos.y + 0.05 - 0.12 * a.deadT;
      return;
    }
    const moving = a.state !== 'graze';
    m.rotation.z = 0;
    m.position.y = a.pos.y + (moving ? Math.abs(Math.sin(a.bob)) * 0.045 : 0);
    // grazing head-dip reads as gentle body pitch on these little blobs
    m.rotation.x = a.state === 'graze' ? Math.sin(a.bob * 0.5) * 0.06 + 0.08 : 0;
  }

  _flee(a, from) {
    const was = a.state;
    a.state = 'flee';
    a.t = 4 + Math.random() * 3;
    if (was !== 'flee') {
      this._call(a, a.sp.alarm, 0.55, a.species === 'chicken' ? 1 : 1.15);
      a.callT = 1.5 + Math.random() * 2;
    }
  }

  scare(pos, radius) {
    for (const a of this.animals) {
      if (a.state === 'dead') continue;
      if (a.sp.predator && a.hp > 25) continue; // wolves don't spook — they commit
      if (Math.hypot(a.pos.x - pos.x, a.pos.z - pos.z) < radius) this._flee(a, pos);
    }
  }

  // same contract as dummies.meleeHit — returns hits.
  //
  // This was the reach + 0.4 / dot 0.5 copy of the arc test. It is now the
  // shared one (src/world/melee-arc.mjs), which adds the y band — a goat on
  // the terrace above you is no longer in reach — and the occlusion test, so a
  // swing no longer kills a sheep through a barn wall. damage() is untouched.
  meleeHit(playerPos, heading, reach, dmg) {
    let hits = 0;
    const swing = meleeSwing(this.world, playerPos, heading, reach);
    for (const a of this.animals) {
      if (a.state === 'dead') continue;
      if (!swing.test(a.pos.x, a.pos.y, a.pos.z, a)) continue;
      this.damage(a, dmg);
      hits++;
    }
    return hits;
  }

  damage(a, dmg) {
    if (a.state === 'dead') return;
    if (a.remote) {
      // server-authoritative wolf: report the hit, the verdict comes back
      // through the wolves broadcast (state change or death)
      this._call(a, a.sp.voice, 0.55, 1.25);
      this.onNet?.({ k: 'wolf-dmg', id: a.id, dmg });
      return;
    }
    a.hp -= dmg;
    this._call(a, a.sp.voice, 0.55, 1.25); // pained cry, pitched up
    this.scare(a.pos, 30); // the herd bolts
    if (a.hp <= 0) {
      a.state = 'dead';
      this.dead.add(a.id);
      this.onNet?.({ k: 'animal', id: a.id });
    } else if (a.sp.predator) {
      // a hurt wolf turns on you — unless it's badly wounded, then it runs
      if (a.hp < 25) this._flee(a, a.pos);
      else if (a.state !== 'attack') { a.state = 'attack'; this._call(a, a.sp.alarm, 0.65, 0.95); }
    } else {
      this._flee(a, a.pos);
    }
  }

  // ---- multiplayer sync ----
  applyDead(id) {
    this.dead.add(id);
    const a = this.animals.find((x) => x.id === id);
    if (a && a.state !== 'dead') a.state = 'dead';
  }

  applyButcher(id) {
    this.dead.add(id);
    this.butchered.add(id);
    const a = this.animals.find((x) => x.id === id);
    if (a) {
      a.mesh.dispose();
      this.animals.splice(this.animals.indexOf(a), 1);
    }
  }

  // corpse interaction for the shared G-key path
  nearestCorpse(pos, reach = 2.6) {
    let best = null, bestD = reach;
    for (const a of this.animals) {
      if (a.state !== 'dead' || this.butchered.has(a.id)) continue;
      const d = Math.hypot(a.pos.x - pos.x, a.pos.z - pos.z);
      if (d < bestD) { best = a; bestD = d; }
    }
    return best;
  }

  butcher(a, inventory) {
    const [meat, n] = a.sp.meat;
    if (!inventory.addItem(meat, n)) return { ok: false, msg: 'Inventory full' };
    this.butchered.add(a.id);
    a.mesh.dispose();
    const i = this.animals.indexOf(a);
    if (i >= 0) this.animals.splice(i, 1);
    this.onNet?.({ k: 'butcher', id: a.id });
    return { ok: true, msg: `Butchered the ${a.sp.label.toLowerCase()} — got ${n}× raw meat` };
  }
}
